/**
 * The date picker's Time and Repeat sub-views.
 */
import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { TextInput } from "@/components/ui/text-input";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui/surface-contract";
import { useInsideModalSurface } from "@/components/ui/modal-surface-context";
import { useT } from "@/i18n";
import { panelsTaskScheduleMessages } from "@/i18n/messages/panels-task-schedule";
import { repeatLabel, type RepeatOption } from "./repeat-options";

/* Compact menu-item row (shortcuts, presets, repeat options, clear actions). */
export const MENU_ROW_CLASS =
  "denker-glass-hover flex w-full min-w-0 items-center rounded-control-sm px-appkit-xs py-1.5 text-appkit-small text-secondary";

export const CLEAR_BUTTON_CLASS =
  "w-full rounded-control-sm px-appkit-xs py-1 text-center text-appkit-mini text-muted transition-colors hover:text-primary";

const TIME_PRESETS = [
  { id: "morning", time: "09:00" },
  { id: "afternoon", time: "13:00" },
  { id: "evening", time: "18:00" },
] as const;

/** The menu surface every view of the picker sits on. */
export function DatePickerSurface({ children }: { children: ReactNode }) {
  // Inside a modal the menu z source promotes it above the modal.
  const insideModal = useInsideModalSurface();
  return (
    <div
      className={cn(
        surfaceRoleClassName("menu"),
        "w-64 overflow-hidden p-appkit-2xs",
      )}
      data-testid="date-picker-dropdown"
      {...surfaceRoleAttributes("menu", {
        nativeLevel: "root",
        nativeGroup: "menus",
      })}
      data-modal-menu={insideModal ? "true" : undefined}
    >
      {children}
    </div>
  );
}

function SubviewHeader({
  title,
  onBack,
  testId,
}: {
  title: string;
  onBack: () => void;
  testId: string;
}) {
  const t = useT(panelsTaskScheduleMessages);
  return (
    <div className="mb-appkit-xs flex min-w-0 items-center gap-appkit-xs px-1">
      <button
        type="button"
        onClick={onBack}
        aria-label={t("back")}
        className="shrink-0 text-muted transition-colors hover:text-primary"
        data-testid={testId}
      >
        <Icons.ChevronLeft className="h-3.5 w-3.5" />
      </button>
      <span className="truncate text-appkit-small font-medium text-primary">
        {title}
      </span>
    </div>
  );
}

export function DatePickerTimeView({
  timeValue,
  onBack,
  onPreset,
  onCustomTime,
  onClear,
}: {
  timeValue: string;
  onBack: () => void;
  onPreset: (time: string) => void;
  onCustomTime: (time: string) => void;
  onClear: () => void;
}) {
  const t = useT(panelsTaskScheduleMessages);
  return (
    <DatePickerSurface>
      <SubviewHeader
        title={t("time")}
        onBack={onBack}
        testId="date-picker-time-back"
      />
      <div className="space-y-0.5">
        {TIME_PRESETS.map((p) => (
          <button
            key={p.time}
            type="button"
            onClick={() => onPreset(p.time)}
            className={cn(
              MENU_ROW_CLASS,
              "justify-between gap-appkit-xs",
              timeValue === p.time && "text-accent",
            )}
            data-testid={`date-picker-time-${p.id}`}
          >
            <span className="min-w-0 truncate">{t(p.id)}</span>
            <span className="shrink-0 text-muted">{p.time}</span>
          </button>
        ))}
      </div>
      <div className="my-appkit-xs border-t border-glass-stroke-subtle" />
      <div className="px-appkit-xs">
        <label className="mb-1 block text-appkit-mini text-muted">
          {t("customTime")}
        </label>
        <TextInput
          type="time"
          value={timeValue || "12:00"}
          onChange={(e) => onCustomTime(e.target.value)}
          className="h-8 text-appkit-small"
          data-testid="date-picker-time-input"
        />
      </div>
      {timeValue && (
        <>
          <div className="my-appkit-xs border-t border-glass-stroke-subtle" />
          <button
            type="button"
            onClick={onClear}
            className={CLEAR_BUTTON_CLASS}
            data-testid="date-picker-time-clear"
          >
            {t("removeTime")}
          </button>
        </>
      )}
    </DatePickerSurface>
  );
}

export function DatePickerRepeatView({
  options,
  repeat,
  onBack,
  onSelect,
  onClear,
}: {
  options: RepeatOption[];
  repeat?: RepeatOption | null;
  onBack: () => void;
  onSelect: (option: RepeatOption) => void;
  onClear: () => void;
}) {
  const t = useT(panelsTaskScheduleMessages);
  return (
    <DatePickerSurface>
      <SubviewHeader
        title={t("repeat")}
        onBack={onBack}
        testId="date-picker-repeat-back"
      />
      <div className="space-y-0.5">
        {options.map((opt) => (
          <button
            key={opt.rrule}
            type="button"
            onClick={() => onSelect(opt)}
            className={cn(
              MENU_ROW_CLASS,
              "text-left",
              repeat?.rrule === opt.rrule && "text-accent",
            )}
            data-testid={`date-picker-repeat-${opt.rrule}`}
          >
            {repeatLabel(opt)}
          </button>
        ))}
      </div>
      {repeat && (
        <>
          <div className="my-appkit-xs border-t border-glass-stroke-subtle" />
          <button
            type="button"
            onClick={onClear}
            className={CLEAR_BUTTON_CLASS}
            data-testid="date-picker-repeat-clear"
          >
            {t("removeRepeat")}
          </button>
        </>
      )}
    </DatePickerSurface>
  );
}
